import { GambytUser } from '@Components/users/types'
import { NavigateFunction, NonIndexRouteObject, UIMatch } from 'react-router-dom'
import { AuthenticatedApp } from '../../AuthenticatedApp'
import { AuthScopes, FeatureFlag } from '@Generated/back-office-client/src'
import { NotFound } from '@Components/not-found'
import { getRouteList, RouteGroup } from './route-list'
import { DropdownMenu, Text } from '@radix-ui/themes'
import { Routes } from './types'
import { maxBy } from 'lodash'

export type BaseRouteConfig = {
  routerConfig: NonIndexRouteObject
}

export type RouteConfig = BaseRouteConfig & {
  scope?: AuthScopes
  /** Any one of these grants access. */
  scopes?: AuthScopes[]
  /** All of these are required. Use when a route needs a combination, not an alternative. */
  requiredScopes?: AuthScopes[]
  feature?: FeatureFlag
  gambytAdminOnly?: boolean
  childrenRoutes?: ChildRouteConfig[]
}

export type ParentRouteConfig = RouteConfig & {
  routeGroup: RouteGroup
}

export type ChildRouteConfig = RouteConfig & {
  submenuDisplayName?: string
  submenuRoute?: Routes
}

const baseRoute: BaseRouteConfig = {
  routerConfig: {
    path: '/',
    Component: AuthenticatedApp,
    children: [{ path: '*', Component: NotFound }],
  },
}

/**
 * Recursivly traverse all routes in the route tree and filter out the ones that we aren't allowed to access
 */
const getAccessibleRoutes = (route: RouteConfig, user: GambytUser) => {
  if (route.gambytAdminOnly && !user.isGambytAdmin) {
    return null
  }

  // `scope` and `scopes` are alternatives — any one of them grants access. `requiredScopes` is a
  // conjunction, for routes that need a combination (e.g. report access AND claim center access to
  // download a bulk export of claim form PII).
  const anyOfScopes = [...(route.scope ? [route.scope] : []), ...(route.scopes ?? [])]
  const hasAnyScope = anyOfScopes.length
    ? anyOfScopes.some((scope) => user.scopes.includes(scope))
    : true

  const hasAllRequiredScopes = route.requiredScopes?.length
    ? route.requiredScopes.every((scope) => user.scopes.includes(scope))
    : true

  const hasRequiredFeature = route.feature ? user.enabledFeatures.includes(route.feature) : true

  if (hasAnyScope && hasAllRequiredScopes && hasRequiredFeature) {
    if (route.childrenRoutes) {
      const accessibleRoutes = route.childrenRoutes.filter((cr) => getAccessibleRoutes(cr, user))
      const routeWithChildren: RouteConfig = {
        ...route,
        childrenRoutes: accessibleRoutes,
      }
      return routeWithChildren
    }

    return route
  }
}

export const getRouteConfigForUser = (
  user: GambytUser | null,
): BaseRouteConfig[] | RouteConfig[] => {
  if (!user) {
    return [baseRoute]
  }

  const appRoutes = getRouteList().reduce((acc: RouteConfig[], route) => {
    const accessibleRoutes = getAccessibleRoutes(route, user)
    if (accessibleRoutes) {
      acc = [...acc, accessibleRoutes]
    }

    return acc
  }, [])

  return appRoutes
}

/**
 * Recursivly traverse all routes that we were able to allow access for above and transform them into an object
 * that is specific to react-router
 */
export const getRouteObjectForUser = (routeConfig: RouteConfig) => {
  if (routeConfig.childrenRoutes) {
    const routeWithChildren: NonIndexRouteObject = {
      ...routeConfig.routerConfig,
      children: routeConfig.childrenRoutes
        ? routeConfig.childrenRoutes.map((cr) => getRouteObjectForUser(cr))
        : undefined,
    }

    return routeWithChildren
  }

  return routeConfig.routerConfig
}

export const getRoutesForUser = (user: GambytUser | null) => {
  const routeConfigs = getRouteConfigForUser(user)

  const appRoutes = routeConfigs.reduce((acc: NonIndexRouteObject[], route) => {
    acc = [...acc, getRouteObjectForUser(route)]

    return acc
  }, [])

  return [
    {
      ...baseRoute.routerConfig,
      children: [...appRoutes, ...(baseRoute.routerConfig.children ?? [])],
    },
  ]
}

/**
 * The submenu entry the current path sits under, or undefined when none does. The deepest match
 * wins: a nested route's path contains its parent's, so both would otherwise read as active.
 */
export const findActiveSubmenuRoute = (
  submenuRoutes: ChildRouteConfig[],
  currentRoute: string,
): ChildRouteConfig | undefined =>
  maxBy(
    submenuRoutes.filter((r) => `${currentRoute}/`.includes(`${r.submenuRoute!}/`)),
    (r) => r.submenuRoute!.length,
  )

export const getNavMenu = (
  routeMatches: UIMatch[],
  routes: (BaseRouteConfig | ParentRouteConfig)[],
  navigate: NavigateFunction,
) => {
  if (!routes) {
    return null
  }

  const routeGroupings = routes.reduce(
    (acc, route) => {
      if (!('routeGroup' in route)) {
        return acc
      }

      if (route.routeGroup && acc[route.routeGroup]) {
        acc[route.routeGroup].push(route)
      } else if (route.routeGroup) {
        acc[route.routeGroup] = [route]
      }

      return acc
    },
    {} as Record<RouteGroup, Array<RouteConfig>>,
  )

  return Object.keys(routeGroupings)
    .map((routeGroup) => {
      const routeGroupConfigs = routeGroupings[routeGroup as RouteGroup]

      const submenuRoutes = routeGroupConfigs
        .flatMap((config) =>
          config.childrenRoutes?.filter((cr) => cr.submenuDisplayName && cr.submenuRoute),
        )
        .filter((x) => !!x)

      const currentRoute = routeMatches[routeMatches.length - 1].pathname

      const activeRoute = findActiveSubmenuRoute(submenuRoutes, currentRoute)

      return submenuRoutes?.length ? (
        <DropdownMenu.Root key={routeGroup}>
          <DropdownMenu.Trigger>
            <div className={`tab-nav tab-nav${activeRoute ? '__active' : ''}`}>
              <Text style={{ cursor: 'pointer' }}>{routeGroup}</Text>
            </div>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content className="tab-nav-dropdown" sideOffset={0}>
            <div className="tab-nav-submenu">
              {submenuRoutes.map((cr) => {
                const activeChildRoute = cr.submenuRoute === activeRoute?.submenuRoute
                return (
                  <DropdownMenu.Item
                    key={cr.submenuDisplayName}
                    className={`tab-nav-submenu__item tab-nav-submenu__item${activeChildRoute ? '--active' : ''}`}
                    onSelect={() => navigate(cr.submenuRoute!, { replace: true })}>
                    <Text weight={activeChildRoute ? 'bold' : 'medium'}>
                      {cr.submenuDisplayName}
                    </Text>
                  </DropdownMenu.Item>
                )
              })}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      ) : (
        <div key={routeGroup} className={`tab-nav tab-nav${activeRoute ? '__active' : ''}`}>
          <Text>{routeGroup}</Text>
        </div>
      )
    })
    .filter((x) => !!x)
}
